<!--
Copyright 2017 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <modal id="field-key-revoke" :state="state" :hideable="!awaitingResponse"
    backdrop @hide="$emit('hide')">
    <template #title>{{ $t('title') }}</template>
    <template #body>
      <div class="modal-introduction">
        <i18n tag="p" path="introduction[0]">
          <template #displayName>
            <strong>{{ fieldKey != null ? fieldKey.displayName : '' }}</strong>
          </template>
        </i18n>
        <p>{{ $t('introduction[1]') }}</p>
        <p>{{ $t('introduction[2]') }}</p>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-danger"
          :disabled="awaitingResponse" @click="revoke">
          {{ $t('action.yesProceed') }} <spinner :state="awaitingResponse"/>
        </button>
        <button type="button" class="btn btn-link" :disabled="awaitingResponse"
          @click="$emit('hide')">
          {{ $t('action.noCancel') }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../modal.vue';
import Spinner from '../spinner.vue';
import request from '../../mixins/request';
import { apiPaths } from '../../util/request';
import { noop } from '../../util/util';

export default {
  name: 'FieldKeyRevoke',
  components: { Modal, Spinner },
  mixins: [request()],
  props: {
    state: {
      type: Boolean,
      default: false
    },
    fieldKey: Object // eslint-disable-line vue/require-default-prop
  },
  data() {
    return {
      awaitingResponse: false
    };
  },
  methods: {
    revoke() {
      this.request({
        method: 'DELETE',
        url: apiPaths.session(this.fieldKey.token)
      })
        .then(() => {
          this.$emit('success', this.fieldKey);
        })
        .catch(noop);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is the title at the top of a pop-up.
    "title": "Revoke User Access",
    "introduction": [
      // {displayName} is formatted in bold.
      "Are you sure you want to revoke access from the App User {displayName}?",
      "Existing Submissions from this user will remain, but anybody relying on this user will have to create a new one to continue downloading Forms or uploading Submissions.",
      "This action cannot be undone."
    ]
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Odebrat přístup uživatele",
    "introduction": [
      "Opravdu chcete odebrat přístup uživateli aplikace {displayName}?",
      "Stávající příspěvky od tohoto uživatele zůstanou, ale kdokoli se na tohoto uživatele obrátí, bude muset vytvořit nové, aby pokračoval ve stahování formulářů nebo odesílání příspěvků.",
      "Tuto akci nelze vrátit zpět."
    ]
  },
  "de": {
    "title": "Zugriffsberechtigung entziehen",
    "introduction": [
      "Wollen Sie dem Benutzer {displayName} wirklich die Zugriffsberechtigung entziehen?",
      "Bestehende Übermittlungen dieses Benutzers bleiben erhalten. Bei jedem Bezug auf diesen Benutzer muss ein neuer erstellt werden, um Formulare herunterzuladen oder Übermittlungen einzureichen.",
      "Diese Aktion kann nicht rückgängig gemacht werden."
    ]
  },
  "es": {
    "title": "Revocar acceso de usuario",
    "introduction": [
      "¿Está seguro de que desea revocar el acceso del usuario móvil {displayName}?",
      "Los envíos existentes de este usuario permanecerán, pero cualquiera que confíe en este usuario tendrá que crear uno nuevo para continuar descargando formularios o cargando envíos.",
      "Esta acción no se puede deshacer."
    ]
  },
  "fr": {
    "title": "Révoquer l'accès des utilisateurs",
    "introduction": [
      "Êtes vous sûr de vouloir révoquer l'accès de l'utilisateur mobile {displayName}?",
      "Les soumissions existantes de cet utilisateur seront conservées, mais toute personne s'appuyant sur cet utilisateur devra en créer une nouvelle pour continuer à télécharger des formulaires ou des soumissions.",
      "Cette action ne peut être annulée."
    ]
  }
}
</i18n>
